import { Trans } from '@lingui/react/macro'
import { Box, Group, Stack, Text, Tooltip } from '@mantine/core'
import { formatShortDate } from '@markku/utils'
import { IconInfoCircle } from '@tabler/icons-react'
import { useCadence } from './cadence'
import type { Service } from './utils'

/**
 * Status + cadence for one service. The supplementary detail — exact cadence in
 * days and the maintenance-plan deadline (eräpäivä) — lives in a single tooltip
 * covering the whole cell, flagged with an ⓘ icon so the hover is discoverable.
 */
export function StatusCell({
  service,
  statusLabel,
  color,
  size = 'sm',
  fw = 600,
  align,
}: {
  service: Service
  statusLabel: string
  color: string
  size?: 'sm' | 'xs'
  fw?: number
  align?: 'right'
}) {
  const {
    label: cadence,
    tooltip: exactDays,
    seasonRange,
  } = useCadence(service)
  const dueBy = service.nextDueDate
    ? formatShortDate(service.nextDueDate)
    : null
  const hasTip = !!(exactDays || seasonRange || dueBy)
  const right = align === 'right'
  const ta = right ? 'right' : undefined

  const body = (
    <Box style={hasTip ? { cursor: 'help' } : undefined}>
      <Group gap={4} wrap="nowrap" justify={right ? 'flex-end' : undefined}>
        <Text size={size} fw={fw} c={`${color}.7`} ta={ta}>
          {statusLabel}
        </Text>
        {hasTip && (
          <IconInfoCircle size={13} color="var(--mantine-color-gray-5)" />
        )}
      </Group>
      <Text size="xs" c="dimmed" ta={ta}>
        {cadence}
      </Text>
    </Box>
  )

  if (!hasTip) return body
  return (
    <Tooltip
      withArrow
      label={
        <Stack gap={2}>
          {exactDays && <Text size="xs">{exactDays}</Text>}
          {seasonRange && <Text size="xs">{seasonRange}</Text>}
          {dueBy && (
            <Text size="xs">
              <Trans>By {dueBy}</Trans>
            </Text>
          )}
        </Stack>
      }
    >
      {body}
    </Tooltip>
  )
}
